// On-device storage for assessment forms.
//
// Every edit a therapist makes is written here before anything is sent to the
// server, so closing the tab, losing signal or the token expiring cannot lose
// an answer. Records are encrypted with AES-GCM under a per-user key that is
// generated in the browser as NON-extractable: script can use it to encrypt and
// decrypt, but it can never be read out, so copying the browser profile's files
// does not expose clinical answers. Nothing here is visible to the therapist --
// there is no PIN or prompt.
//
// What this does not protect against: someone using an unlocked, signed-in
// device. That is the device lock's job.
//
// Stores
//   keys   { userId, key: CryptoKey }
//   forms  { uuid, userId, updatedAt, pendingCount, iv, data }   (data encrypted)
//   cache  { id, value }                                          (non-sensitive, e.g. form definitions)

const DB_NAME = 'wmf-assessments';
const DB_VERSION = 1;

let dbPromise = null;

function openDb() {
  if (!dbPromise) {
    dbPromise = new Promise((resolve, reject) => {
      if (typeof indexedDB === 'undefined' || !window.crypto?.subtle) {
        reject(new Error('This browser cannot store drafts on the device'));
        return;
      }
      const req = indexedDB.open(DB_NAME, DB_VERSION);
      req.onupgradeneeded = () => {
        const db = req.result;
        if (!db.objectStoreNames.contains('keys')) db.createObjectStore('keys', { keyPath: 'userId' });
        if (!db.objectStoreNames.contains('forms')) {
          const forms = db.createObjectStore('forms', { keyPath: 'uuid' });
          forms.createIndex('userId', 'userId');
        }
        if (!db.objectStoreNames.contains('cache')) db.createObjectStore('cache', { keyPath: 'id' });
      };
      req.onsuccess = () => resolve(req.result);
      req.onerror = () => reject(req.error);
      req.onblocked = () => reject(new Error('Device storage is blocked by another tab'));
    }).catch((err) => {
      dbPromise = null;
      throw err;
    });
  }
  return dbPromise;
}

function tx(storeNames, mode, fn) {
  return openDb().then((db) => new Promise((resolve, reject) => {
    const t = db.transaction(storeNames, mode);
    let result;
    Promise.resolve(fn(t)).then((r) => { result = r; }, reject);
    t.oncomplete = () => resolve(result);
    t.onerror = () => reject(t.error);
    t.onabort = () => reject(t.error || new Error('Device storage transaction aborted'));
  }));
}

function request(req) {
  return new Promise((resolve, reject) => {
    req.onsuccess = () => resolve(req.result);
    req.onerror = () => reject(req.error);
  });
}

// ---------------------------------------------------------------------------
// Keys and encryption
// ---------------------------------------------------------------------------

const keyCache = new Map();

async function getKey(userId) {
  const id = String(userId);
  if (keyCache.has(id)) return keyCache.get(id);
  const promise = (async () => {
    const existing = await tx(['keys'], 'readonly', (t) => request(t.objectStore('keys').get(id)));
    if (existing?.key) return existing.key;
    const key = await window.crypto.subtle.generateKey(
      { name: 'AES-GCM', length: 256 }, false, ['encrypt', 'decrypt'],
    );
    // Two tabs could race to create a key; keep whichever landed first so
    // records encrypted by either remain readable.
    return tx(['keys'], 'readwrite', async (t) => {
      const store = t.objectStore('keys');
      const raced = await request(store.get(id));
      if (raced?.key) return raced.key;
      store.put({ userId: id, key });
      return key;
    });
  })();
  keyCache.set(id, promise);
  promise.catch(() => keyCache.delete(id));
  return promise;
}

async function encrypt(userId, value) {
  const key = await getKey(userId);
  const iv = window.crypto.getRandomValues(new Uint8Array(12));
  const data = await window.crypto.subtle.encrypt(
    { name: 'AES-GCM', iv }, key, new TextEncoder().encode(JSON.stringify(value)),
  );
  return { iv, data };
}

async function decrypt(userId, iv, data) {
  const key = await getKey(userId);
  const plain = await window.crypto.subtle.decrypt({ name: 'AES-GCM', iv }, key, data);
  return JSON.parse(new TextDecoder().decode(plain));
}

// ---------------------------------------------------------------------------
// Merging two copies of a record
// ---------------------------------------------------------------------------

// Two tabs (or a tab and a background sync) can each hold a copy of the same
// form. Saving one must never drop an unsynced edit that only the other has,
// so pending edits are merged per field, newest wins, and the server snapshot
// with the higher revision is kept.
export function mergeRecords(stored, incoming) {
  if (!stored) return incoming;
  const pending = { ...(stored.pending || {}) };
  Object.entries(incoming.pending || {}).forEach(([k, e]) => {
    if (!pending[k] || e.t >= pending[k].t) pending[k] = e;
  });
  // A key the incoming copy just synced (removed from its pending) must not be
  // resurrected from an older stored copy carrying the same edit.
  (incoming.syncedKeys || []).forEach(({ key, t }) => {
    if (pending[key] && pending[key].t <= t) delete pending[key];
  });
  const server = (stored.server?.revision ?? -1) > (incoming.server?.revision ?? -1)
    ? stored.server : incoming.server;
  return { ...stored, ...incoming, server, pending, syncedKeys: undefined };
}

// ---------------------------------------------------------------------------
// Public API
// ---------------------------------------------------------------------------

export async function isAvailable() {
  try {
    await openDb();
    return true;
  } catch {
    return false;
  }
}

// Ask the browser not to evict this origin's storage under pressure. Best
// effort: browsers may decline, and Safari ignores it for non-installed sites.
export async function requestPersistence() {
  try {
    if (navigator.storage?.persist && !(await navigator.storage.persisted())) {
      await navigator.storage.persist();
    }
  } catch { /* best effort */ }
}

export async function loadForm(userId, uuid) {
  const row = await tx(['forms'], 'readonly', (t) => request(t.objectStore('forms').get(uuid)));
  if (!row || String(row.userId) !== String(userId)) return null;
  return decrypt(userId, row.iv, row.data);
}

export async function listForms(userId) {
  const rows = await tx(['forms'], 'readonly',
    (t) => request(t.objectStore('forms').index('userId').getAll(String(userId))));
  const out = [];
  for (const row of rows) {
    try {
      out.push(await decrypt(userId, row.iv, row.data));
    } catch (err) {
      // Never delete what cannot be read; just leave it out of the list.
      console.error('Could not read a locally stored assessment', row.uuid, err);
    }
  }
  return out;
}

// Writes are serialised per form so a slow write can never land after, and
// overwrite, a newer one.
const writeChains = new Map();

export function saveForm(userId, record) {
  const uuid = record.uuid;
  const prev = writeChains.get(uuid) || Promise.resolve();
  const next = prev.catch(() => {}).then(async () => {
    const storedRow = await tx(['forms'], 'readonly', (t) => request(t.objectStore('forms').get(uuid)));
    let stored = null;
    if (storedRow && String(storedRow.userId) === String(userId)) {
      try { stored = await decrypt(userId, storedRow.iv, storedRow.data); } catch { stored = null; }
    }
    const merged = mergeRecords(stored, { ...record, userId: String(userId), updatedAt: Date.now() });
    const { iv, data } = await encrypt(userId, merged);
    await tx(['forms'], 'readwrite', (t) => {
      t.objectStore('forms').put({
        uuid,
        userId: String(userId),
        updatedAt: merged.updatedAt,
        pendingCount: Object.keys(merged.pending || {}).length,
        iv,
        data,
      });
    });
    return merged;
  });
  writeChains.set(uuid, next);
  next.finally(() => { if (writeChains.get(uuid) === next) writeChains.delete(uuid); });
  return next;
}

export async function deleteForm(uuid) {
  await (writeChains.get(uuid) || Promise.resolve()).catch(() => {});
  return tx(['forms'], 'readwrite', (t) => { t.objectStore('forms').delete(uuid); });
}

export async function getCache(id) {
  const row = await tx(['cache'], 'readonly', (t) => request(t.objectStore('cache').get(id)));
  return row ? row.value : null;
}

export function putCache(id, value) {
  return tx(['cache'], 'readwrite', (t) => { t.objectStore('cache').put({ id, value }); });
}
